import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Lookup from '@/api/lookup/api-lookup';
import { firstListItem, apiPath } from '@/constants/defaultValues';
import { Delete, Get, Post, Put } from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { getLabel } from '@/helpers/Utils';

const initialState = {
  leaveDefinitionList: [],
  employeeLeaveDefinitionList: [],
  employeeHrLeaveDefinitionList: [],
  leaveTypeItem: {},
  loading: false,
  error: '',
};
export const fetchHrEmployeeLeave = createAsyncThunk(
  'leaveType/fetchHrEmployeeLeave',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.GetAllEmployeeLeaves({
        companyID: getCompanyID(),
        targetEmployee: {
          targetEmployeesTypeID: 2,
          targetEmployeesID: params?.employeeID,
        },
        fromDate:params?.fromDate, 
        toDate: params?.toDate
      });
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item?.leaveTypeResponse?.nameEn,
          nameAr: item?.leaveTypeResponse?.nameAr,
          code: item.code,
          label: getLabel(
            item?.leaveTypeResponse?.nameAr,
            item?.leaveTypeResponse?.nameEn
          ),
          value: item.id,
          key: item.id,
          workFlowID: item.workFlowID,
          leaveTypeID: item.leaveTypeID,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const fetchEmployeeLeave = createAsyncThunk(
  'leaveType/fetchEmployeeLeave',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.GetAllEmployeeLeaves({
        companyID: getCompanyID(),
        targetEmployee: {
          targetEmployeesTypeID: 2,
          targetEmployeesID: getEmployeeID(),
        },
        ...params
      });
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item?.leaveTypeResponse?.nameEn,
          nameAr: item?.leaveTypeResponse?.nameAr,
          code: item.code,
          label: getLabel(
            item?.leaveTypeResponse?.nameAr,
            item?.leaveTypeResponse?.nameEn
          ),
          value: item.id,
          key: item.id,
          workFlowID: item.workFlowID,
          leaveTypeID: item.leaveTypeID,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const fetchLeaveDefinition = createAsyncThunk(
  'leaveDefinition/fetchLeaveDefinition',
  async (params, thunkAPI) => {
    try {
      const response = await Lookup.GetLeaveDefinition();
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item?.leaveTypeResponse.nameEn,
          nameAr: item?.leaveTypeResponse.nameAr,
          code: item.code,
          label: getLabel(
            item.leaveTypeResponse.nameAr,
            item.leaveTypeResponse.nameEn
          ),
          value: item.id,
          leaveTypeID: item.leaveTypeID,
          key: item.id,
          workFlowID: item.workFlowID,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getLeaveTypeById = createAsyncThunk(
  'leaveDefinition/getLeaveTypeById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/LeaveType/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const leaveDefinitionSlice = createSlice({
  name: 'leaveDefinition',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaveDefinition.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeaveDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveDefinitionList = action.payload;
        state.error = '';
      })
      .addCase(fetchLeaveDefinition.rejected, (state, action) => {
        state.loading = false;
        state.leaveDefinitionList = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(fetchEmployeeLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmployeeLeave.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeLeaveDefinitionList = action.payload;
        state.error = '';
      })
      .addCase(fetchEmployeeLeave.rejected, (state, action) => {
        state.loading = false;
        state.employeeLeaveDefinitionList = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(fetchHrEmployeeLeave.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHrEmployeeLeave.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeHrLeaveDefinitionList = action.payload;
        state.error = '';
      })
      .addCase(fetchHrEmployeeLeave.rejected, (state, action) => {
        state.loading = false;
        state.employeeHrLeaveDefinitionList = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(getLeaveTypeById.pending, (state) => {
        state.loading = true;
        state.leaveTypeItem = {};
      })
      .addCase(getLeaveTypeById.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveTypeItem = action.payload;
        state.error = '';
      })
      .addCase(getLeaveTypeById.rejected, (state, action) => {
        state.loading = false;
        state.leaveTypeItem = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});

export default leaveDefinitionSlice.reducer;
